import { useState, useEffect } from "react";

// paths
import { AUTH_PATHS } from "src/routes/path";

// custom components
import LoadingScreen from "src/components/LoadingScreen";
import LoginForm from "src/components/auth/LoginForm";
import Page from "src/components/Page";

// hooks
import useAuth from "src/hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";

// mui
import { Stack, Link, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";

function InvalidOrExpiredLinkView() {
  return (
    <>
      <Typography mb={"8px"} variant={"subtitle1"} fontSize={28}>
        This link has been used or expired.
      </Typography>
      <Typography>
          Your email address has already been verified.<br/>
          Please click the button below to log in to your account. Welcome to Vinuage!
      </Typography>
      <LoadingButton
        component={Link}
        href={AUTH_PATHS.login}
        loading={false}
        type={"submit"}
        sx={{
          borderRadius: "8px",
          px: 9,
          py: 1.2,
          mt: "48px",
          alignSelf: "center",
          width: "100%",
        }}
        variant={"contained"}
      >
        Log in
      </LoadingButton>
    </>
  );
}

export default function Login({ _message }) {
  const { login, verifyAccount } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [verificationLinkExp, setVerificationLinkExp] = useState(null);
  const navigate = useNavigate();

  const location = useLocation();
  const navStateMessage = location.state?.message;
  const [message, setMessage] = useState(navStateMessage || _message);

  let { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const uid = searchParams.get("uid");
  const token = searchParams.get("token");
  const access_token = searchParams.get("access_token");
  const refresh_token = searchParams.get("refresh_token");

  useEffect(() => {
    const initialize = async () => {
      // Handle token-based auto-login
      if (access_token && refresh_token) {
        try {
          setIsLoading(true);
          // Set the tokens in localStorage
          localStorage.setItem("access", access_token);
          localStorage.setItem("refresh", refresh_token);
          // Navigate to root which will redirect to dashboard
          navigate("/");
          window.location.reload();
          return;
        } catch (error) {
          console.error("Error during auto-login:", error);
          setMessage("Auto-login failed. Please log in manually.");
          setIsLoading(false);
        }
      }

      // Handle email verification
      if (uid && token) {
        try {
          await verifyAccount(uid, token);
          setMessage("Your email address has been verified. Welcome to Vinuage!");
        } catch (error) {
          setVerificationLinkExp(true);
          console.log("error occurred verifying account:", error.response.data);
        }
      }
      
      setIsLoading(false);
    };

    initialize();
  }, [uid, token, access_token, refresh_token, verifyAccount, navigate]);

  return isLoading ? (
    <LoadingScreen />
  ) : (
    <Page sx={{ p: 2 }} title={"Login"}>
      <Stack
        p={0}
        sx={{
          position: "relative",
          top: "50px",
          direction: "row",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {verificationLinkExp === true ? (
          <InvalidOrExpiredLinkView />
        ) : (
          <LoginForm login={login} message={message} />
        )}
      </Stack>
    </Page>
  );
}
